import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailPageHeader,
  MiIcon,
  MiList,
  MiModalConfirm,
} from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_LABELS,
  BUTTON_VARIANTS,
  MODAL_TITLES,
  PAGINATION,
  PERMISSIONS,
} from "@miplatform/constants";
import {
  mdiChevronLeft,
  mdiPencilOutline,
  mdiPlus,
  mdiTrashCanOutline,
} from "@mdi/js";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, useTheme } from "@mui/material";
import { AddTradeDrawer } from "./AddTradeDrawer/AddTradeDrawer";
import { useManageTradeViewModel } from "./useManageTradeViewModel";

export const ManageTrade = () => {
  const [modal, setModal] = useState({
    addModal: false,
    editModal: false,
    deleteModal: false,
  });
  const [selectedItem, setSelectedItem] = useState(null);

  const { hasPermission } = usePermissions();

  const {
    tradeList,
    loading,
    getTradeList,

    updateTrade,
    deleteTrade,
    createTrade,
  } = useManageTradeViewModel();

  const { classes } = useStyles();
  const theme = useTheme();

  useEffect(() => {
    getTradeList();
  }, []);

  const hasEditPermission = hasPermission(PERMISSIONS.TITLE.UPDATE);

  const tradeFieldset = [
    {
      field: "name",
      headerName: "Name",
      headerAlign: "left",
      align: "left",
      flex: 1,
    },
  ];

  const onCreate = async (data) => {
    const result = await createTrade(data);
    if (result) {
      setModal({});
      getTradeList();
    }
  };

  const onUpdate = async (id, data) => {
    const result = await updateTrade(id, data);
    if (result) {
      setSelectedItem(null);
      setModal({});
      getTradeList();
    }
  };

  const onDelete = async () => {
    const result = await deleteTrade(selectedItem.id, {
      modifiedOn: selectedItem.modifiedOn,
    });
    if (result) {
      setModal({});
      setSelectedItem(null);
      getTradeList();
    }
  };

  const onDeleteCancel = () => {
    setModal({});
    setSelectedItem(null);
  };

  const getRowActions = (row) => [
    hasEditPermission && {
      text: BUTTON_LABELS.EDIT,
      onClick: () => {
        setSelectedItem(row);
        setModal({
          open: true,
          handleSubmit: (data) => onUpdate(row.id, data),
          title: "Edit Trade",
        });
      },
      renderIcon: () => (
        <MiIcon
          path={mdiPencilOutline}
          size={1}
          color={theme.palette.green.main}
        />
      ),
    },
    {
      text: BUTTON_LABELS.DELETE,
      onClick: () => {
        setSelectedItem(row);
        setModal({ open: true, deleteModal: true });
      },
      renderIcon: () => (
        <MiIcon
          path={mdiTrashCanOutline}
          size={1}
          color={theme.palette.red.main}
        />
      ),
    },
  ];

  return (
    <Box className={classes.root}>
      <div className={classes.contentContainer}>
        <MiDetailPageHeader
          title="Trades"
          titleIcon={mdiChevronLeft}
          loading={loading}
          actionComponent={
            hasPermission(PERMISSIONS.TITLE.CREATE) && (
              <MiButton
                title="Add Trade"
                variant={BUTTON_VARIANTS.CONTAINED}
                color={BUTTON_COLORS.PRIMARY}
                startIcon={{
                  path: mdiPlus,
                  iconSize: 0.8,
                }}
                onClick={() =>
                  setModal({
                    open: true,
                    handleSubmit: onCreate,
                    title: "Add Trade",
                  })
                }
              />
            )
          }
        />
        <Box className={classes.container}>
          <MiList
            columns={tradeFieldset}
            data={tradeList}
            paginationMode={PAGINATION.CLIENT}
            loading={loading}
            getRowActions={
              hasPermission(PERMISSIONS.TRADE.DELETE) && getRowActions
            }
            onRowClick={(row) => {
              setSelectedItem(row.row);
              setModal({
                open: true,
                handleSubmit: (data) => onUpdate(row.row?.id, data),
                title: "Edit Trade",
              });
            }}
            data-testid="trades-table"
          />
        </Box>
        <AddTradeDrawer
          onClose={() => setModal({})}
          onSubmit={modal.handleSubmit}
          loading={loading}
          title={modal.title}
          trade={selectedItem}
          open={modal.open && !modal.deleteModal}
        />
        <MiModalConfirm
          title={MODAL_TITLES.DELETE}
          description={`Are you sure you want to delete trade: ${selectedItem?.name}?`}
          open={modal.open && modal.deleteModal}
          handlePositive={onDelete}
          handleNegative={onDeleteCancel}
          className={classes.modal}
          textPositive={BUTTON_LABELS.DELETE}
        />
      </div>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    height: "inherit",
    display: "flex",
  },
  container: {
    height: "inherit",
    overflowY: "scroll",
    margin: 5,
    borderRadius: 10,
    padding: 5,
    backgroundColor: theme.palette.common.white,
  },
  contentContainer: { display: "flex", flexDirection: "column", width: "100%" },
  modal: {
    minWidth: 400,
  },
}));
